<template>
  <div class="order-details">
    <v-card class="card">
      <div class="order-summary">
        <div style="display: flex; align-items: center; gap: 16px;">
          <h2 class="order-summary__title">
            {{ $t("message.payments.orderDetails") }}
          </h2>
          <PeriodSwitch v-if="getSelectedPeriod" />
        </div>
        <PlanSelector v-if="paymentPlans" :selected-plan="selectedPlan.plan" />
        <QuantitySelector
          v-if="locationsQuantity"
          :value="locationsQuantity"
          :price-per-unit="getPricePerLocation"
          @input="setLoader(), debouncedUpdateQuantity($event)"
        />
        <template v-if="+price !== 0">
          <PaymentMethodSelector />
          <div class="section">
            <label>{{ $t("message.payments.promoCode") }}</label>
            <div>
              <input
                :value="promotionCode"
                type="text"
                class="coupon-input"
                :placeholder="$t('message.payments.promoCode')"
                @change="changeHandler"
              />
            </div>
          </div>
        </template>
        <div class="order-summary__total-price">
          <span>{{ $t("message.payments.totalPrice") }}</span>
          <div class="order-summary__price-wrapper">
            <div class="order-summary__price-value">
              <template v-if="!isPriceLoading">{{ price }}</template>
              <div v-else class="loader">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
              {{ currency }}
            </div>
            <span class="order-summary__period">{{
              `${$t("message.payments.net")} ${period[getSelectedPeriod]}`
            }}</span>
            <div v-if="!isPriceLoading && +price" class="order-summary__tax">
              (+{{ taxPrice }} {{ currency }} {{ $t("message.payments.vat") }}
              {{ taxPercentage }}%)
            </div>
          </div>
        </div>
      </div>
      <v-checkbox
        v-model="checkbox"
        class="order-details__checkbox"
        hide-details
      >
        <template v-slot:label>
          <div>
            {{ $t("message.payments.accept") }}
            <a target="_blank" :href="termsOfUseLink.path" @click.stop>
              {{ $t("message.payments.paymentsRegulations") }}
            </a>
          </div>
        </template>
      </v-checkbox>
      <ui-button
        class="order-details__submit"
        type="submit"
        color="primary"
        block
        :disabled="!checkbox"
        :loading="isSubmitLoading"
        @click="submitHandler"
        >{{ $t("message.common.further") }}</ui-button
      >
    </v-card>
    <SectionLoader :status="isInitialLoading" :opacity="1" />
  </div>
</template>

<script>
import PeriodSwitch from "@/components/Payments/PeriodSwitch.vue";
import PlanSelector from "@/components/Payments/PlanSelector.vue";
import PaymentMethodSelector from "@/components/Payments/PaymentMethodSelector.vue";
import QuantitySelector from "@/components/Payments/QuantitySelector.vue";
import debounce from "lodash/debounce";
import serviceHandler from "@/helpers/handlers/service";
import PaymentsService from "@/services/payments";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import constants from "@/config/constants.js";
import usersService from "@/services/users";

const PLAN_NAMES = ["free", "basic", "standard", "pro", "enterprise"];
export default {
  name: "OrderDetails",
  components: {
    PeriodSwitch,
    PlanSelector,
    QuantitySelector,
    PaymentMethodSelector,
    SectionLoader
  },

  data() {
    return {
      quantity: 1,
      isPriceLoading: true,
      isInitialLoading: true,
      checkbox: false,
      isSubmitLoading: false,
      coupon: "",
      regulations: {
        name: this.$t("message.session.register.regulations"),
        path: { en: constants.regulationsEn, pl: constants.regulations }
      }
    };
  },

  computed: {
    currentLanguage() {
      return this.$i18n.locale;
    },
    termsOfUseLink() {
      return {
        name: this.regulations.name,
        path: this.regulations.path[this.currentLanguage]
      };
    },
    planChangeType() {
      return this.$store.state.payments.planChangeType;
    },
    selectedPaymentMethod() {
      return this.$store.state.payments.selectedPaymentMethod;
    },
    isFreeTrial() {
      return this.$store.getters["auth/isFreeTrial"];
    },
    locationsQuantityFromLimitsUsage() {
      return this.$store.getters["payments/locationsQuantityFromLimitsUsage"];
    },
    period() {
      return {
        month: this.$t("message.payments.monthly").toLowerCase(),
        year: this.$t("message.payments.annually").toLowerCase()
      };
    },
    currency() {
      return this.$store.state.payments.currency;
    },
    taxPercentage() {
      return this.$store.getters["payments/taxPercentage"];
    },
    selectedPlan() {
      return this.$store.state.payments.selectedPlan;
    },
    promotionCode() {
      return this.$store.state.payments.promoCode;
    },
    promotionCodeFromList() {
      return this.$store.getters["payments/promotionCode"];
    },
    price() {
      return `${this.$store.getters["payments/price"]}`;
    },
    taxPrice() {
      return `${this.$store.getters["payments/taxPrice"]}`;
    },
    getSelectedPeriod() {
      return this.$store.state.payments.selectedPeriod;
    },
    getPlanPerPeriod() {
      if (!this.getSelectedPeriod) return {};
      return this.$store.getters["payments/getPlanPerPeriod"](
        this.selectedPlan.product_id,
        this.getSelectedPeriod
      );
    },
    getPricePerLocation() {
      return this.$store.getters["payments/getPricePerLocation"](
        this.getPlanPerPeriod,
        this.locationsQuantity
      );
    },
    paymentPlans() {
      return this.$store.getters["payments/paymentPlans"];
    },
    locationsQuantity() {
      return this.$store.state.payments.selectedLocationsCount;
    },
    initialChosenPlan() {
      return this.$store.state.payments.initialChosenPlan;
    },
    priceId() {
      return this.$store.getters["payments/getPriceId"];
    },
    selectedPeriod() {
      return this.$store.state.payments.selectedPeriod;
    }
  },
  async created() {
    try {
      await this.initializePayments();
      this.initializeDebouncedUpdateQuantity();
      await this.handlePricePreview();
    } finally {
      this.isInitialLoading = false;
      this.isPriceLoading = false;
    }
  },

  methods: {
    submitHandler() {
      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.SUBSCRIPTION_PLAN_CONFIGURED,
        promotion_code: this.promotionCode || null,
        price_list: this.initialChosenPlan
          ? this.initialChosenPlan.price_list
          : null,
        selected_plan: PLAN_NAMES.includes(this.selectedPlan.name.toLowerCase())
          ? this.selectedPlan.name.toLowerCase()
          : "other",
        plan_change_type: this.planChangeType,
        is_upgrade_from_free_trial: this.isFreeTrial,
        selected_locations_limit: this.locationsQuantity,
        selected_payment_cycle: this.selectedPeriod,
        selected_payment_method:
          this.selectedPaymentMethod.value === "p24"
            ? "fast_bank_transfers"
            : this.selectedPaymentMethod.value,
        currency: this.currency,
        ...(this.$store.state.company.company.country_code && {
          countryCode: this.$store.state.company.company.country_code
        }),
        current_locations_limit: this.selectedPlan.limits.max_facilities || null
      });

      +this.price === 0
        ? this.submitFreeOrder()
        : this.$router.push({ name: "invoice-data" });
    },
    changeHandler(event) {
      this.$store.dispatch("payments/setPromoCode", event.target.value);
    },
    async initializePayments() {
      try {
        await this.$store.dispatch("payments/getInitialChosenPlan");
      } catch (error) {
        // console.error("No initial chosen plan");
      }
      await this.$store.dispatch("payments/getPlans");

      if (this.initialChosenPlan && this.initialChosenPlan.campaign) {
        await this.$store.dispatch("payments/getPromotionCodes");
        this.$store.dispatch(
          "payments/setPromoCode",
          this.promotionCodeFromList
        );
        this.coupon = this.promotionCode;
      }

      this.setPlanDetails(this.initialChosenPlan);
    },

    setPlanDetails(initialChosenPlan) {
      const selectedPlan =
        initialChosenPlan && initialChosenPlan.product_name
          ? this.paymentPlans.find(
              plan =>
                plan.name.toLowerCase() ===
                initialChosenPlan.product_name.toLowerCase()
            )
          : this.paymentPlans.find(
              plan => plan.name.toLowerCase() === "standard"
            ) || this.paymentPlans[0];

      this.$store.dispatch("payments/setSelectedPlan", selectedPlan);
      const initialPlanIndex = this.paymentPlans.findIndex(
        plan => plan.product_id === this.selectedPlan.product_id
      );
      this.$store.dispatch("payments/setInitialPlanIndex", initialPlanIndex);
      const locationsCount =
        this.locationsQuantityFromLimitsUsage && this.isFreeTrial
          ? this.locationsQuantityFromLimitsUsage
          : initialChosenPlan && initialChosenPlan.quantity
          ? initialChosenPlan.quantity
          : 1;

      this.$store.dispatch(
        "payments/setSelectedLocationsCount",
        locationsCount
      );

      const period =
        initialChosenPlan && initialChosenPlan.period
          ? { 1: "month", 2: "year" }[initialChosenPlan.period]
          : "month";

      this.$store.dispatch("payments/setSelectedPeriod", period);
    },
    initializeDebouncedUpdateQuantity() {
      this.debouncedUpdateQuantity = debounce(this.updateQuantity, 500);
    },
    async handlePricePreview() {
      if (this.selectedPlan.product_id) {
        await this.getPricePreview();
      }
    },
    setLoader() {
      this.isPriceLoading = true;
    },
    async getPricePreview() {
      await this.$store.dispatch("payments/getPricePreview");
    },
    async updateQuantity(quantity) {
      this.$store.dispatch("payments/setSelectedLocationsCount", quantity);
      await this.getPricePreview();
      this.isPriceLoading = false;
    },
    async updateCompanyProfile(callback) {
      let company_subscription;
      const interval = setInterval(async () => {
        const response = await serviceHandler(usersService.getUserDetails());
        company_subscription = response.company_subscription;
        if (
          company_subscription &&
          company_subscription.expires_at &&
          company_subscription.name
        ) {
          clearInterval(interval);
          this.$store.dispatch(
            "auth/setPlanRestrictions",
            company_subscription
          );
          callback();
        }
      }, 1000);
    },
    async submitFreeOrder() {
      this.isSubmitLoading = true;
      const data = await serviceHandler(
        PaymentsService.postOrder({
          method_type: "card",
          currency: this.currency,
          items: [
            {
              price: this.priceId,
              quantity: this.locationsQuantity
            }
          ]
        })
      );

      if (data.is_paid) {
        await this.$store.dispatch("company/fetchCompanies");
        await this.updateCompanyProfile(() => {
          this.$router.push({ name: "dashboard" });
        });
      } else {
        this.$notify({
          group: "default",
          type: "error",
          text: this.$t("message.payments.status.failed")
        });
      }
      this.isSubmitLoading = false;
    }
  },
  watch: {
    selectedPlan: {
      async handler() {
        if (this.isInitialLoading) return;
        this.isPriceLoading = true;
        await this.getPricePreview();
        this.isPriceLoading = false;
      },
      deep: true
    },
    async getSelectedPeriod() {
      this.$store.dispatch("payments/setPromoCode", this.promotionCodeFromList);
      if (this.isInitialLoading) return;
      this.isPriceLoading = true;
      await this.getPricePreview();
      this.isPriceLoading = false;
    },
    async promotionCode(current) {
      if (this.isInitialLoading) return;
      this.isPriceLoading = true;
      await this.getPricePreview();
      this.isPriceLoading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.order-details {
  display: flex;
  flex-direction: column;
  gap: $spacing * 3;

  &__checkbox {
    margin-top: 0;
    ::v-deep label {
      @include font-size(14px);
      color: $dark-blue-3;

      a {
        color: $primary;
        font-weight: 600;
      }
    }
  }

  * {
    color: $dark-blue-3;
  }

  &__submit {
    align-self: flex-end;
  }
}

.card {
  padding: 3 * $spacing 4 * $spacing;
  display: flex;
  flex-direction: column;
  gap: $spacing * 2;
  height: auto;
}

.order-summary {
  display: flex;
  gap: $spacing * 3;
  flex-direction: column;
  border-radius: 7px !important;
  width: 100%;

  &__price-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    gap: $spacing * 0.5;
  }

  &__period,
  &__tax {
    align-self: flex-end;
  }

  &__tax {
    margin-top: $spacing * 0.25;
    @include font-size(14px);
    color: $gray;
  }

  &__title {
    @include font-size(24px);
  }

  &__price-value {
    @include font-size(30px);
    display: flex;
    font-weight: bold;
    gap: $spacing;
  }

  &__total-price {
    padding: $spacing * 3 0 $spacing * 0.5 0;
    border-top: 1px dashed $gray-300;
    display: flex;
    justify-content: space-between;
    align-items: center;

    * {
      line-height: 1em;
    }

    span {
      @include font-size(14px);
      font-weight: 500;
    }
  }
}

.loader {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  margin: 0 3px;
  background-color: #333;
  border-radius: 50%;
  animation: dot-bounce 1.4s infinite both;
}

.dot:nth-child(1) {
  animation-delay: -0.32s;
}

.dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes dot-bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.section {
  @include font-size(14px);
  display: flex;
  flex-direction: column;
  gap: $spacing;

  label {
    line-height: 1em;
  }

  &__text {
    font-weight: 600;
  }
}

.coupon-input {
  height: 46px;
  border: 1px solid #dadada;
  border-radius: 8px;
  padding: 0 $spacing * 2.5;

  &:focus {
    border-color: $primary;
    outline: none;
  }
}
</style>
